import { ReactGridContainer } from '@europrocurement/flexy-components';
import { DossierOpenSearchJsonldOpensearchDossierStatutRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import {
    DOSSIER_STATUS_ARCHIVED,
    getDossierStatus,
} from '@europrocurement/l2d-modules/modules/Dossiers';
import { faFolder } from '@fortawesome/pro-regular-svg-icons';
import { Box, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { type ResizeHandleAxis } from 'react-grid-layout';
import { useStatusStyle } from '@europrocurement/l2d-modules/hooks';
import WidgetWithAction from '../molecules/WidgetWithAction/WidgetWithAction';
import DashboardStatCardTotalBar from '../molecules/DashboardStatCardTotalBar/DashboardStatCardTotalBar';

type DossierStatusWidgetDashboardProps = {
    statusDossierOpenSearch: DossierOpenSearchJsonldOpensearchDossierStatutRead[];
    handleDossierStatusClick: (statutId?: number | null) => void;
};
const DossierStatusWidgetDashboard: React.FunctionComponent<DossierStatusWidgetDashboardProps> =
    function ({ statusDossierOpenSearch = [], handleDossierStatusClick }) {
        const { palette } = useTheme();
        const { getDossierStatusStyle } = useStatusStyle();
        const [statusDossiers, setStatusDossiers] = useState<
            DossierOpenSearchJsonldOpensearchDossierStatutRead[]
        >(
            getDossierStatus().filter(
                (dossStatus) => ![DOSSIER_STATUS_ARCHIVED].includes(dossStatus.id as number),
            ),
        ); // Empty list of Dossier status with a count as 0

        const maxXindexCard = 5;
        const currentXIndexCard = useRef(2); // Initial position at the 3 column
        const currentYIndexCard = useRef(0);

        useEffect(() => {
            setStatusDossiers(
                getDossierStatus()
                    .filter(
                        (dossStatus) =>
                            ![DOSSIER_STATUS_ARCHIVED].includes(dossStatus.id as number),
                    )
                    .map((statusDossier) => ({
                        ...statusDossier,
                        childsCount: statusDossierOpenSearch.find(
                            (statusOpenSearch) => statusOpenSearch.id === statusDossier.id,
                        )
                            ? statusDossierOpenSearch.find(
                                  (statusOpenSearch) => statusOpenSearch.id === statusDossier.id,
                              )?.childsCount
                            : statusDossier.childsCount,
                    })),
            );
        }, [statusDossierOpenSearch]);

        return (
            <ReactGridContainer
                localStorageName="grid-status-dossier-formalist"
                gridconfig={{ cols: 6, rowHeight: 200 }}
                initialXIndexCard={2}
                currentXIndexCard={currentXIndexCard}
                currentYIndexCard={currentYIndexCard}
                // loop thru status dossier to generate a layout of key and position in react grid
                layoutProps={[
                    {
                        i: 'dossier_status_recap',
                        x: 0,
                        y: 0,
                        w: 3,
                        h: 1,
                        isResizable: true,
                        resizeHandles: [
                            's',
                            'w',
                            'e',
                            'n',
                            'sw',
                            'nw',
                            'se',
                            'ne',
                        ] as ResizeHandleAxis[],
                    },
                    ...statusDossiers.map((statusDossier, i) => {
                        if (i !== 0 && i % maxXindexCard === 0) {
                            // In the first row there is a space for 3 items, in the others row there is space for 6 items
                            currentXIndexCard.current = -1;
                            currentYIndexCard.current++;
                        }
                        currentXIndexCard.current++;
                        return {
                            i: `dossier_status_${(statusDossier.id as number).toString()}`,
                            x: currentXIndexCard.current,
                            y: currentYIndexCard.current,
                            w: 1,
                            h: 1,
                            isResizable: true,
                            resizeHandles: [
                                's',
                                'w',
                                'e',
                                'n',
                                'sw',
                                'nw',
                                'se',
                                'ne',
                            ] as ResizeHandleAxis[],
                        };
                    }),
                ]}
            >
                <Box
                    key="dossier_status_recap"
                    sx={{
                        width: '100%', // Width and height based on parent size
                        height: '100%',
                        background: `linear-gradient(to bottom, ${palette.grey[200]},  ${palette.grey[50]})`,
                        border: `1px solid ${palette.grey[400]}`,
                        boxShadow: `0px 2px 2.5px 0px ${palette.grey[200]}`,
                        borderRadius: '15px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: '4px',
                    }}
                >
                    <DashboardStatCardTotalBar
                        items={statusDossiers}
                        title="Suivi dossier — flux actif"
                        icon={faFolder}
                    />
                </Box>
                {statusDossiers.map((statusDossier) => {
                    const { paletteString, namedIcon, sxStatus } = getDossierStatusStyle(
                        statusDossier.id as number,
                    );
                    return (
                        <Box key={`dossier_status_${(statusDossier.id as number).toString()}`}>
                            <WidgetWithAction
                                title={statusDossier.libelle as string}
                                icon={namedIcon}
                                actionMessage="Voir"
                                actionCallback={async () =>
                                    handleDossierStatusClick(statusDossier.id as number)
                                }
                                data={statusDossier.childsCount || '0'}
                                color={paletteString}
                                sx={sxStatus}
                            />
                        </Box>
                    );
                })}
            </ReactGridContainer>
        );
    };

export default DossierStatusWidgetDashboard;
